/**
 * Guided-tour state — localStorage "urava_tours_v1" (tour spec §5/§6).
 *
 * Deliberately client-side only (zero backend): a new device re-offers the
 * tours, which is arguably a feature. All reads/writes are try/catch-safe so
 * a blocked localStorage (private mode) degrades to "tours always offered".
 */

export type TourSeenState = 'completed' | 'dismissed';

export interface TourResume {
  tourId: string;
  stepIndex: number;
}

export interface TourStorageState {
  /** Terminal state per tour id. */
  seen: Record<string, TourSeenState>;
  /** Saved mid-tour exit point — "resume, never restart" (spec §5). */
  resume: TourResume | null;
  /** Resume snackbar is offered once per saved resume (spec §5). */
  resumeOffered: Record<string, boolean>;
  /** Global "Don't show tips again" — kills AUTO tours/coach marks only. */
  optOut: boolean;
  /** Browser-session count (drives the Cmd+K coach mark's 5th-session gate). */
  sessions: number;
}

const KEY = 'urava_tours_v1';
const SESSION_COUNTED_KEY = 'urava_tour_session_counted';

const DEFAULTS: TourStorageState = {
  seen: {},
  resume: null,
  resumeOffered: {},
  optOut: false,
  sessions: 0,
};

export function loadTourState(): TourStorageState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<TourStorageState>;
    return {
      seen: parsed.seen && typeof parsed.seen === 'object' ? parsed.seen : {},
      resume:
        parsed.resume && typeof parsed.resume.tourId === 'string'
          ? { tourId: parsed.resume.tourId, stepIndex: Number(parsed.resume.stepIndex) || 0 }
          : null,
      resumeOffered:
        parsed.resumeOffered && typeof parsed.resumeOffered === 'object'
          ? parsed.resumeOffered
          : {},
      optOut: !!parsed.optOut,
      sessions: Number(parsed.sessions) || 0,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(state: TourStorageState) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode etc. — tours simply re-offer */
  }
}

export function markTourSeen(tourId: string, state: TourSeenState) {
  const s = loadTourState();
  s.seen[tourId] = state;
  if (s.resume?.tourId === tourId) s.resume = null;
  save(s);
}

export function clearTourSeen(tourId: string) {
  const s = loadTourState();
  delete s.seen[tourId];
  if (s.resume?.tourId === tourId) s.resume = null;
  delete s.resumeOffered[tourId];
  save(s);
}

export function setTourResume(tourId: string, stepIndex: number) {
  const s = loadTourState();
  s.resume = { tourId, stepIndex };
  save(s);
}

export function clearTourResume() {
  const s = loadTourState();
  s.resume = null;
  save(s);
}

export function markResumeOffered(tourId: string) {
  const s = loadTourState();
  s.resumeOffered[tourId] = true;
  save(s);
}

export function setTourOptOut(optOut: boolean) {
  const s = loadTourState();
  s.optOut = optOut;
  save(s);
}

/**
 * Count one browser session (guarded by sessionStorage so refreshes within
 * the same tab session don't inflate the count). Returns the new total.
 */
export function bumpTourSession(): number {
  const s = loadTourState();
  try {
    if (sessionStorage.getItem(SESSION_COUNTED_KEY)) return s.sessions;
    sessionStorage.setItem(SESSION_COUNTED_KEY, '1');
  } catch {
    return s.sessions;
  }
  s.sessions += 1;
  save(s);
  return s.sessions;
}
